// Message protocol between main thread and sim worker, plus command/replay types.
// Commands are the ONLY way anything (player, UI, AI, replays) mutates the sim.
// v5 (M4): skirmish config (AI, factions, win condition, mirror), research,
// monument, game result. The AI issues commands through applyCommand directly
// inside the sim (deterministic), so the replay log stays player-only.

import type { MapSizeName } from './constants';

// ---- Skirmish configuration (DESIGN.md §8.2, §9, §10; flaw fix #5) ----

export type AiDifficulty = 'none' | 'easy' | 'normal' | 'hard';
export type WinCondition = 'conquest' | 'monument' | 'score';

export interface GameConfig {
  seed: number;
  mapSize: MapSizeName;
  /** point-reflection mirrored map for fairness (DESIGN.md §3.4) */
  mirror: boolean;
  playerFaction: number; // index into data/factions.json
  enemyFaction: number;
  aiDifficulty: AiDifficulty; // 'none' = sandbox (no AI opponent)
  winCondition: WinCondition;
  scoreMinutes: number; // time limit for 'score' games (20/40/60)
}

export function defaultConfig(seed: number, mapSize: MapSizeName): GameConfig {
  return {
    seed,
    mapSize,
    mirror: false,
    playerFaction: 0,
    enemyFaction: (seed >>> 0) % 3,
    aiDifficulty: 'none',
    winCondition: 'conquest',
    scoreMinutes: 40,
  };
}

// ---- Commands (tick-stamped by the sim when enqueued) ----

export type Command =
  | { type: 'placeBuilding'; def: string; x: number; y: number } // x,y = footprint origin
  | { type: 'buildRoad'; tiles: number[] }
  | { type: 'claimHex'; hex: number }
  | { type: 'surveyHex'; hex: number }
  | { type: 'trainSurveyor' }
  | { type: 'setPriority'; building: number; level: 0 | 1 | 2 | 3 } // Low/Normal/High/Urgent
  | { type: 'setRecipe'; building: number; recipe: number }
  | { type: 'setWarePriority'; ware: number } // bump ware to top of the global tiebreak order
  // ---- military (M3, DESIGN.md §7) ----
  | { type: 'trainScout' } // HQ: 1 pop, no ware (§4.3)
  | { type: 'recruit'; building: number; kind: number; count: number } // batch queue ±count (§7.2)
  | { type: 'setVeteran'; building: number; on: boolean } // veteran recruitment toggle (§7.3)
  | { type: 'setRally'; building: number; tile: number } // -1 clears
  | { type: 'moveUnits'; ids: number[]; tile: number; attack: boolean } // move / attack-move (§7.5)
  | { type: 'garrisonUnits'; ids: number[]; building: number } // walk in (§7.4)
  | { type: 'ungarrison'; building: number }
  // ---- research (M4, DESIGN.md §8.1) ----
  | { type: 'setResearch'; building: number; node: number }; // Athenaeum: start a node

export interface TickCommands {
  tick: number;
  commands: Command[];
}

export interface Replay {
  version: number;
  config: GameConfig;
  log: TickCommands[];
}

// ---- Main -> Worker ----

export type MainToWorker =
  | { type: 'init'; config: GameConfig }
  | { type: 'load'; replay: Replay; untilTick: number } // reconstruct via replay, then go live
  | { type: 'commands'; commands: Command[] }
  | { type: 'setSpeed'; speed: 0 | 1 | 2 | 3 }
  | { type: 'getReplay' }
  | { type: 'inspect'; building: number }; // -1 = stop inspecting

// ---- Worker -> Main ----

/** unit snapshot: [id, kind, x, y, carryWareIdx(-1), targetTile(-1), donkey, owner, hpPct, veteran] */
export const UNIT_STRIDE = 10;
/** building snapshot: [id, defIdx, x, y, size, state, progress01, priority, boostTicks, recipe, owner, hpPct] */
export const BUILDING_STRIDE = 12;

export interface MapInfo {
  width: number;
  height: number;
  seed: number;
  hexCols: number;
  hexRows: number;
}

/** final game result (M4, DESIGN.md §8.2); winner -1 = draw */
export interface GameResult {
  winner: number;
  reason: 'conquest' | 'monument' | 'score';
}

export interface StatsMsg {
  type: 'stats';
  tick: number;
  stocks: number[]; // player warehouse stock per wareIdx
  popUsed: number;
  popCap: number;
  surveyorsIdle: number;
  surveyorsTotal: number;
  openRequests: number;
  checksum: number;
  simMsPerTick: number;
  // ---- M4 ----
  result: GameResult | null;
  /** [playerScore, enemyScore] (score win condition; always computed) */
  scores: [number, number];
  /** ticks until score-mode time limit (-1 outside score mode) */
  scoreTicksLeft: number;
  researchedMask: number; // player's completed research (bitmask by node idx)
}

export interface AlertMsg {
  text: string;
  building: number; // id for jump-to (-1 none)
  x: number;
  y: number;
}

/** live detail for the selected-building panel (polled via 'inspect') */
export interface InspectMsg {
  type: 'inspection';
  id: number;
  defIdx: number;
  state: number;
  priority: number;
  recipe: number;
  boostTicks: number;
  stock: number[] | null;
  inputBuf: number[] | null;
  outputBuf: number[] | null;
  delivered: number[];
  // ---- military (M3) ----
  hp: number;
  maxHp: number;
  owner: number;
  garrison: number; // units inside
  garrisonCap: number;
  queue: number[] | null; // per UnitKind queued counts (training grounds)
  veteranMode: boolean;
  trainKind: number; // -1 idle
  trainLeft: number; // ticks
  rallyTile: number; // -1 none
  // ---- research & monument (M4) ----
  cost: number[]; // effective construction cost (current stage for the monument)
  researchNode: number; // active node idx (-1)
  researchLeft: number; // ticks remaining once started (0 = still gathering bars)
  researchedMask: number; // owner's completed research bitmask
  stage: number; // monument stage (0-based)
  stageCount: number; // total monument stages (0 for non-staged)
}

export type WorkerToMain =
  | {
      type: 'map';
      info: MapInfo;
      terrain: Uint8Array;
      elevation: Uint8Array;
      treeDensity: Uint8Array;
      stoneAmount: Uint8Array;
      hexOfTile: Int32Array;
      deposits: Int32Array; // per hex: (kind<<24)|amount
      hexOwner: Int8Array;
      hexSurveyed: Uint8Array;
      roads: Uint8Array;
      fog: Uint8Array; // per-tile FogState (M3)
    }
  | {
      type: 'snapshot';
      tick: number;
      speed: number;
      units: Float32Array;
      unitCount: number;
      buildings: Float32Array;
      buildingCount: number;
    }
  | {
      type: 'events';
      tick: number;
      tilesChanged: Int32Array; // packed [tileIdx, terrain, treeDensity, stoneAmount] * n
      roadsAdded: Int32Array;
      territory?: Int8Array; // full hexOwner when changed
      surveyed?: Uint8Array; // full hexSurveyed when changed
      deposits?: Int32Array; // full deposits when amounts changed
      fog?: Uint8Array; // full per-tile FogState when recomputed (M3)
      shots: Int32Array; // packed [fromX_q8, fromY_q8, toX_q8, toY_q8, kind] * n (render FX)
      deaths: Int32Array; // packed [x_q8, y_q8] * n
      alerts: AlertMsg[];
      rejects: string[];
    }
  | StatsMsg
  | InspectMsg
  | { type: 'loaded'; tick: number } // replay reconstruction finished
  | { type: 'replay'; replay: Replay };
